import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MAX_ENTRY_AMOUNT,
  amountReportLabel,
  payrollPeriodFor,
  shiftPayrollPeriod,
  todayInZone,
  validateAmountEntry,
  type AmountEntry,
  type AmountEntryInput,
  type AmountEntryIssue,
  type AmountEntryOptions,
  type AmountRecurring,
  type AmountReportId,
} from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTimeAdminApi } from '../api';
import { DataTable, type DataColumn } from '../components/DataTable';
import { useIsAdmin } from '../useIsAdmin';

import { PostToNetSuite, type PostPlanView, type PostResultView } from './PostToNetSuite';

/**
 * The table behind Bonus, Commission and Reimbursements.
 *
 * ONE COMPONENT for all three, taking the report as a prop. They are the same
 * record — person, department, pay period, amount, note — differing only in which
 * NetSuite earning the posting step resolves, so three near-identical components
 * would be three places for an entry rule to drift.
 *
 * These figures are typed by a supervisor and PAID by NetSuite, which shapes two
 * decisions here:
 *
 *  - validation runs through the SAME `validateAmountEntry` the API uses, so the
 *    browser can never accept something the server will reject (or vice versa);
 *  - the earning-gap flag is shown ON THE ROW, because an amount whose earning
 *    cannot be resolved will not post at all, and the cheap moment to discover
 *    that is now rather than during the payroll run.
 */

/** "Every period" — the pre-#14 behaviour, still offered for exports and cross-week checks. */
const ALL_PERIODS = 'all';

const useStyles = makeStyles({
  bar: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  spacer: { flexGrow: 1 },
  editor: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  field: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  amount: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  issue: { color: tokens.colorPaletteRedForeground1 },
  /** "Not attempted" — present so the column never reads as an empty cell. */
  muted: { color: tokens.colorNeutralForeground3 },
  periodField: { maxWidth: '320px' },
  recurringBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    marginBottom: tokens.spacingVerticalM,
  },
  recurringNote: { color: tokens.colorNeutralForeground3 },
  recurringRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  selectedRow: { backgroundColor: tokens.colorNeutralBackground1Selected },
});

/** A row being created or edited. `id` absent = a new entry. */
interface Draft {
  id?: string;
  form: AmountEntryInput;
  /**
   * Whether this NEW entry starts a recurrence instead of being a single
   * payment (Michael, 2026-09-12). Only meaningful while creating: an existing
   * entry cannot retroactively become a rule, and a rule is edited by stopping
   * it and starting another.
   */
  repeats?: boolean;
}

/** Money for display. Kept out of the cell so every column formats identically. */
function money(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

interface AmountReportTableProps {
  reportId: AmountReportId;
}

export function AmountReportTable({ reportId }: AmountReportTableProps): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();
  const isAdmin = useIsAdmin();

  /**
   * Which payroll week this report is showing (Michael, 2026-09-17: "The bonuses report
   * needs to be split into the weekly payroll weeks too. Bonuses rewarded between weeks
   * are not the same like I originally thought").
   *
   * Defaults to the CURRENT period rather than everything: an entry is paid against one
   * week's paycheck, so listing several weeks together made two $100 bonuses a week
   * apart read as one person owed $200. ALL is still offered, because an export or a
   * cross-week check wants it.
   */
  const [period, setPeriod] = useState<string>(
    () => payrollPeriodFor(todayInZone(new Date())).endDate
  );
  const [entries, setEntries] = useState<readonly AmountEntry[] | null>(null);
  const [options, setOptions] = useState<AmountEntryOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [issues, setIssues] = useState<readonly AmountEntryIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  /**
   * Standing amounts for this report. Reimbursement only for now — nobody has
   * asked for a repeating bonus, and offering it would invite one.
   */
  const supportsRecurring = reportId === 'reimbursement';
  const [recurring, setRecurring] = useState<readonly AmountRecurring[]>([]);
  const [stoppingId, setStoppingId] = useState<string | null>(null);

  const label = amountReportLabel(reportId);

  const reload = useCallback(async (): Promise<void> => {
    const [list, opts] = await Promise.all([
      api.listAmountEntries(reportId, period === ALL_PERIODS ? undefined : period),
      api.getAmountOptions(reportId),
    ]);
    setEntries(list);
    setOptions(opts);
  }, [api, reportId, period]);

  useEffect(() => {
    let cancelled = false;
    // Clear first: switching report must never show the previous report's rows
    // while the new ones load — on a money table that misread costs real trust.
    setEntries(null);
    setDraft(null);
    setSelectedId(null);
    setLoadError(null);
    void (async () => {
      try {
        const [list, opts] = await Promise.all([
          api.listAmountEntries(reportId, period === ALL_PERIODS ? undefined : period),
          api.getAmountOptions(reportId),
        ]);
        if (!cancelled) {
          setEntries(list);
          setOptions(opts);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : 'Could not load entries.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, reportId, period]);

  const issueFor = useCallback(
    (field: AmountEntryIssue['field']): string | undefined =>
      issues.find((i) => i.field === field)?.message,
    [issues]
  );

  /**
   * This period and the previous eight — the same depth the timecard offers, which is
   * far enough back for a correction without turning the list into a scroll.
   */
  const periodChoices = useMemo(() => {
    const current = payrollPeriodFor(todayInZone(new Date()));
    return Array.from({ length: 9 }, (_, i) => {
      const p = shiftPayrollPeriod(current, -i);
      return {
        value: p.endDate,
        label: i === 0 ? `Current · ${p.startDate} – ${p.endDate}` : `${p.startDate} – ${p.endDate}`,
      };
    });
  }, []);

  const columns: ReadonlyArray<DataColumn<AmountEntry>> = useMemo(
    () => [
      {
        columnId: 'employee',
        header: 'Employee',
        renderCell: (row) => (
          <span>
            {row.employeeName}
            {row.earningFlag !== undefined && (
              // Shown here rather than in its own column so it travels with the
              // person it concerns. An amount with an unresolvable earning will
              // NOT post, so this is a blocker, not a hint.
              <Tooltip
                content={`${row.earningFlag.reason} (department: ${row.earningFlag.departmentName})`}
                relationship="description"
              >
                <Badge appearance="filled" color="danger" size="small">
                  No earning type
                </Badge>
              </Tooltip>
            )}
          </span>
        ),
      },
      { columnId: 'department', header: 'Department', renderCell: (row) => row.departmentName },
      { columnId: 'period', header: 'Pay period ending', renderCell: (row) => row.payPeriodEnding },
      {
        columnId: 'amount',
        header: 'Amount',
        renderCell: (row) => <span className={styles.amount}>{money(row.amount)}</span>,
      },
      {
        columnId: 'postResult',
        header: 'Post result',
        /*
         * Michael, 2026-09-17: "We tried to post commissions and bonuses from WLX and
         * did not get any feedback on if things manually posted and had to recalculate
         * and manually check everything."
         *
         * The reason each outcome carries its MESSAGE and not just a colour: "failed"
         * on its own sends somebody to NetSuite to find out why, which is the work this
         * column exists to remove. A skip in particular is usually actionable — no
         * paycheck in the batch, batch already committed — and the reason names the fix.
         */
        renderCell: (row) => {
          if (row.postStatus === undefined) {
            return <span className={styles.muted}>Not attempted</span>;
          }
          const color =
            row.postStatus === 'posted'
              ? 'success'
              : row.postStatus === 'failed'
                ? 'danger'
                : 'warning';
          const label =
            row.postStatus === 'posted'
              ? 'Posted'
              : row.postStatus === 'failed'
                ? 'Failed'
                : 'Skipped';
          const when =
            row.postAttemptedAt === undefined
              ? ''
              : ` · ${new Date(row.postAttemptedAt).toLocaleString()}`;
          const badge = (
            <Badge appearance="filled" color={color} size="small">
              {label}
            </Badge>
          );
          return row.postMessage === undefined ? (
            <Tooltip content={`Last attempt${when}`} relationship="description">
              {badge}
            </Tooltip>
          ) : (
            <Tooltip content={`${row.postMessage}${when}`} relationship="description">
              {badge}
            </Tooltip>
          );
        },
      },
      { columnId: 'note', header: 'Note', renderCell: (row) => row.note ?? '—' },
    ],
    [styles.amount, styles.muted]
  );

  const loadRecurring = useCallback(async (): Promise<void> => {
    if (!supportsRecurring) {
      return;
    }
    try {
      setRecurring(await api.listRecurringAmounts(reportId));
    } catch {
      // Non-fatal: the entries themselves still load and post. A failed read here
      // must not take the whole report down with it.
      setRecurring([]);
    }
  }, [api, reportId, supportsRecurring]);

  useEffect(() => {
    void loadRecurring();
  }, [loadRecurring]);

  const stopRecurring = useCallback(
    async (id: string): Promise<void> => {
      setStoppingId(id);
      setActionError(null);
      try {
        await api.stopRecurringAmount(reportId, id);
        await loadRecurring();
      } catch (error) {
        setActionError(
          error instanceof Error ? error.message : 'Could not stop the recurring amount.'
        );
      } finally {
        setStoppingId(null);
      }
    },
    [api, loadRecurring, reportId]
  );

  /**
   * Adapters onto the shared posting dialog. They exist so the dialog knows
   * nothing about amount entries — it renders a plan and reports a result, and
   * both shapes are flattened here rather than branching inside it.
   */
  const loadPostPlan = useCallback(
    async (payPeriodEnding: string): Promise<PostPlanView> => {
      const plan = await api.getAmountPostPreview(reportId, payPeriodEnding);
      return {
        batchNumber: plan.batchNumber,
        totalToPost: plan.totalToPost,
        rows: plan.rows.map((r) => ({
          key: r.entryId,
          name: r.employeeName,
          amount: r.amount,
          action: r.action,
          reason: r.reason,
        })),
      };
    },
    [api, reportId]
  );

  const runPost = useCallback(
    async (payPeriodEnding: string): Promise<PostResultView> => {
      const result = await api.postAmounts(reportId, payPeriodEnding);
      return {
        posted: result.posted.map((p) => ({ name: p.employeeName, amount: p.amount })),
        failed: result.failed.map((f) => ({ name: f.employeeName, reason: f.reason })),
      };
    },
    [api, reportId]
  );

  const startNew = useCallback((): void => {
    setIssues([]);
    setActionError(null);
    setDraft({
      form: { employeeId: '', departmentId: '', payPeriodEnding: '', amount: 0, note: null },
      repeats: false,
    });
  }, []);

  const startEdit = useCallback((): void => {
    const row = entries?.find((e) => e.id === selectedId);
    if (row === undefined) {
      return;
    }
    setIssues([]);
    setActionError(null);
    setDraft({
      id: row.id,
      form: {
        employeeId: row.employeeId,
        departmentId: row.departmentId,
        payPeriodEnding: row.payPeriodEnding,
        amount: row.amount,
        note: row.note,
      },
    });
  }, [entries, selectedId]);

  const submit = useCallback(async (): Promise<void> => {
    if (draft === null || options === null) {
      return;
    }
    // Validate with the SAME rules the API applies, so a rejection here is never a
    // surprise there. The API re-runs them regardless — this is a courtesy, not a
    // boundary.
    const found = validateAmountEntry(draft.form, {
      activeEmployeeIds: new Set(options.employees.map((e) => e.id)),
      activeDepartmentIds: new Set(options.departments.map((d) => d.id)),
    });
    setIssues(found);
    if (found.length > 0) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      if (draft.id === undefined && draft.repeats === true) {
        // A recurrence carries no pay period — it applies to every period from
        // its start date until stopped, and materialises this period's entry on
        // the next load.
        await api.createRecurringAmount(reportId, {
          employeeId: draft.form.employeeId,
          departmentId: draft.form.departmentId,
          amount: draft.form.amount,
          note: draft.form.note ?? null,
          startsOn: draft.form.payPeriodEnding,
        });
        await loadRecurring();
      } else if (draft.id === undefined) {
        await api.createAmountEntry(reportId, draft.form);
      } else {
        const { employeeId: _ignored, ...rest } = draft.form;
        await api.updateAmountEntry(reportId, draft.id, rest);
      }
      setDraft(null);
      await reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save the entry.');
    } finally {
      setSaving(false);
    }
  }, [api, draft, options, reload, reportId]);

  const remove = useCallback(async (): Promise<void> => {
    if (selectedId === null) {
      return;
    }
    setSaving(true);
    setActionError(null);
    try {
      await api.deleteAmountEntry(reportId, selectedId);
      setSelectedId(null);
      setConfirmDelete(false);
      await reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not delete the entry.');
    } finally {
      setSaving(false);
    }
  }, [api, reload, reportId, selectedId]);

  const patch = useCallback((change: Partial<AmountEntryInput>): void => {
    setDraft((current) =>
      current === null ? null : { ...current, form: { ...current.form, ...change } }
    );
  }, []);

  if (loadError !== null) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{loadError}</MessageBarBody>
      </MessageBar>
    );
  }
  if (entries === null || options === null) {
    return <Spinner label={`Loading ${label}…`} />;
  }

  return (
    <div>
      <div className={styles.bar}>
        <Button appearance="primary" onClick={startNew} disabled={draft !== null || saving}>
          New {label} entry
        </Button>
        <Button onClick={startEdit} disabled={selectedId === null || draft !== null || saving}>
          Edit
        </Button>
        <Button
          onClick={() => {
            setConfirmDelete(true);
          }}
          disabled={selectedId === null || draft !== null || saving}
        >
          Delete
        </Button>
        <span className={styles.spacer} />
        <Text>
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </Text>
        {isAdmin && (
          // Admin only, matching the API: this is the one control here that puts
          // money on a cheque. Bonus amounts are approved OUTSIDE WorkLogix
          // before they are entered (Michael, 2026-09-13), so there is no second
          // approval step in the app — the plan dialog is the review.
          <PostToNetSuite
            what={label.toLowerCase()}
            /*
             * The dialog OPENS on the week this table is showing (Michael, 2026-09-30:
             * "It keeps defaulting to the current payroll period ... Abbye is having to
             * correct the payroll period twice"). "All periods" is not postable — a post
             * names one week — so it seeds nothing and the dialog falls back to the
             * current week, visibly, in its own picker.
             */
            defaultPayPeriodEnding={period === ALL_PERIODS ? undefined : period}
            disabled={draft !== null || saving}
            loadPlan={loadPostPlan}
            post={runPost}
            onPosted={reload}
          />
        )}
      </div>

      {actionError !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{actionError}</MessageBarBody>
        </MessageBar>
      )}

      {draft !== null && (
        <div className={styles.editor}>
          <div className={styles.field}>
            <Text size={200}>Employee</Text>
            <Dropdown
              disabled={draft.id !== undefined}
              value={options.employees.find((e) => e.id === draft.form.employeeId)?.name ?? ''}
              selectedOptions={draft.form.employeeId === '' ? [] : [draft.form.employeeId]}
              onOptionSelect={(_, data) => {
                const chosen = options.employees.find((e) => e.id === data.optionValue);
                // Pre-fill the department from the employee, which is right most of
                // the time and still editable when the amount belongs elsewhere.
                patch({
                  employeeId: data.optionValue ?? '',
                  ...(chosen?.departmentId != null && draft.form.departmentId === ''
                    ? { departmentId: chosen.departmentId }
                    : {}),
                });
              }}
            >
              {options.employees.map((e) => (
                <Option key={e.id} value={e.id}>
                  {e.name}
                </Option>
              ))}
            </Dropdown>
            {issueFor('employeeId') !== undefined && (
              <Text size={200} className={styles.issue}>
                {issueFor('employeeId')}
              </Text>
            )}
          </div>

          <div className={styles.field}>
            <Text size={200}>Department</Text>
            <Dropdown
              value={options.departments.find((d) => d.id === draft.form.departmentId)?.name ?? ''}
              selectedOptions={draft.form.departmentId === '' ? [] : [draft.form.departmentId]}
              onOptionSelect={(_, data) => {
                patch({ departmentId: data.optionValue ?? '' });
              }}
            >
              {options.departments.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Dropdown>
            {issueFor('departmentId') !== undefined && (
              <Text size={200} className={styles.issue}>
                {issueFor('departmentId')}
              </Text>
            )}
          </div>

          {supportsRecurring && draft.id === undefined && (
            // Offered only when CREATING: an existing payment cannot retroactively
            // become a rule, and a rule is changed by stopping it and starting
            // another — which keeps what it already paid explainable.
            <div className={styles.field}>
              <Text size={200}>How often</Text>
              <Dropdown
                value={draft.repeats === true ? 'Repeats every pay period' : 'One time only'}
                selectedOptions={[draft.repeats === true ? 'repeat' : 'once']}
                onOptionSelect={(_, data) => {
                  setDraft((d) =>
                    d === null ? d : { ...d, repeats: data.optionValue === 'repeat' }
                  );
                }}
              >
                <Option value="once" text="One time only">
                  One time only
                </Option>
                <Option value="repeat" text="Repeats every pay period">
                  Repeats every pay period
                </Option>
              </Dropdown>
            </div>
          )}

          <div className={styles.field}>
            <Text size={200}>
              {draft.repeats === true ? 'Starts with pay period ending' : 'Pay period ending'}
            </Text>
            <Input
              type="date"
              value={draft.form.payPeriodEnding}
              onChange={(_, data) => {
                patch({ payPeriodEnding: data.value });
              }}
            />
            {issueFor('payPeriodEnding') !== undefined && (
              <Text size={200} className={styles.issue}>
                {issueFor('payPeriodEnding')}
              </Text>
            )}
          </div>

          <div className={styles.field}>
            <Text size={200}>Amount</Text>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={String(MAX_ENTRY_AMOUNT)}
              value={draft.form.amount === 0 ? '' : String(draft.form.amount)}
              onChange={(_, data) => {
                patch({ amount: data.value === '' ? 0 : Number(data.value) });
              }}
            />
            {issueFor('amount') !== undefined && (
              <Text size={200} className={styles.issue}>
                {issueFor('amount')}
              </Text>
            )}
          </div>

          <div className={styles.field}>
            <Text size={200}>Note</Text>
            <Input
              value={draft.form.note ?? ''}
              onChange={(_, data) => {
                patch({ note: data.value === '' ? null : data.value });
              }}
            />
            {issueFor('note') !== undefined && (
              <Text size={200} className={styles.issue}>
                {issueFor('note')}
              </Text>
            )}
          </div>

          <Button appearance="primary" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            onClick={() => {
              setDraft(null);
              setIssues([]);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
        </div>
      )}

      {supportsRecurring && recurring.filter((r) => r.active).length > 0 && (
        <div className={styles.recurringBox}>
          <Text weight="semibold">Repeating every pay period</Text>
          <Text size={200} className={styles.recurringNote}>
            These create an entry automatically each period. Stopping one leaves the
            entries it already created alone — delete those individually if they should
            not be paid.
          </Text>
          {recurring
            .filter((r) => r.active)
            .map((r) => (
              <div key={r.id} className={styles.recurringRow}>
                <Text>
                  {`${r.employeeName} — ${money(r.amount)} · ${r.departmentName} · since ${r.startsOn}`}
                </Text>
                <Button
                  appearance="secondary"
                  size="small"
                  disabled={stoppingId !== null}
                  onClick={() => void stopRecurring(r.id)}
                >
                  {stoppingId === r.id ? <Spinner size="tiny" /> : 'Stop'}
                </Button>
              </div>
            ))}
        </div>
      )}

      {/*
        * The payroll week this report is showing. Placed immediately above the table
        * because it changes what the table MEANS — the same row set for two different
        * weeks is two different payrolls, and before #14 they were listed together
        * with only a date column to tell them apart.
        */}
      <Field label="Pay period" className={styles.periodField}>
        <Dropdown
          value={period === ALL_PERIODS ? 'All periods' : period}
          selectedOptions={[period]}
          onOptionSelect={(_, d) => {
            setPeriod(d.optionValue ?? ALL_PERIODS);
          }}
        >
          {periodChoices.map((p) => (
            <Option key={p.value} value={p.value} text={p.label}>
              {p.label}
            </Option>
          ))}
          <Option value={ALL_PERIODS} text="All periods">
            All periods
          </Option>
        </Dropdown>
      </Field>

      <DataTable
        columns={columns}
        rows={entries}
        getRowId={(row) => row.id}
        // DataTable has no selection concept of its own, so selection lives here and
        // is expressed back to it as a row class. Clicking the selected row clears
        // it, so there is always a way out without a second control.
        onRowClick={(row) => {
          setSelectedId((current) => (current === row.id ? null : row.id));
        }}
        rowClassName={(row) => (row.id === selectedId ? styles.selectedRow : undefined)}
        emptyMessage={`No ${label.toLowerCase()} entries yet.`}
        showHeaderWhenEmpty
      />

      <Dialog
        open={confirmDelete}
        onOpenChange={(_, data) => {
          setConfirmDelete(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete this entry?</DialogTitle>
            <DialogContent>It will not be paid. This cannot be undone.</DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setConfirmDelete(false);
                }}
              >
                Cancel
              </Button>
              <Button appearance="primary" onClick={() => void remove()} disabled={saving}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
